import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { API_BASE, authFetch } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';

function generatePlotID() {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const randomLetter = letters[Math.floor(Math.random() * letters.length)];
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `${randomLetter}${randomNum}`;
}

function generateRandomBatchNumber() {
  return Math.floor(10000 + Math.random() * 90000);
}

function SubmitModal({ onClose, onSuccess }) {
  const { user, isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [growers, setGrowers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [fields, setFields] = useState([]);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedGrower, setSelectedGrower] = useState(null);
  const [selectedFarm, setSelectedFarm] = useState(null);

  const [samples, setSamples] = useState([]);
  const [editingSampleIndex, setEditingSampleIndex] = useState(null);
  const [displayBatchNumber] = useState(generateRandomBatchNumber());

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Auto-select company when companies are loaded
  useEffect(() => {
    if (companies.length > 0 && !selectedCompany) {
      // For admin users, default to "KAS Internal"
      if (isAdmin()) {
        const kasInternal = companies.find(c => c.company_name === 'KAS Internal');
        if (kasInternal) {
          setSelectedCompany(kasInternal.id);
          fetchGrowers(kasInternal.id);
          return;
        }
      }
      
      // For regular users, auto-select their company
      if (user?.company_id) {
        const userCompany = companies.find(c => c.id === user.company_id);
        if (userCompany) {
          setSelectedCompany(user.company_id);
          fetchGrowers(user.company_id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, user]);

  const fetchCompanies = async () => {
    const res = await authFetch(`${API_BASE}/companies/`);
    const data = await res.json();
    setCompanies(data);
  };

  const fetchGrowers = async (companyId) => {
    try {
      const res = await authFetch(`${API_BASE}/growers/company/${companyId}`);
      if (!res.ok) {
        console.error(`Failed to fetch growers: ${res.status}`);
        setGrowers([]);
        return;
      }
      const data = await res.json();
      setGrowers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching growers:', err);
      setGrowers([]);
    }
  };

  const fetchFarms = async (growerId) => {
    const res = await authFetch(`${API_BASE}/farms/grower/${growerId}`);
    const data = await res.json();
    setFarms(data);
  };

  const fetchFields = async (farmId) => {
    const res = await authFetch(`${API_BASE}/fields/farm/${farmId}`);
    const data = await res.json();
    setFields(data);
  };

  const handleCompanyChange = async (e) => {
    const companyId = parseInt(e.target.value, 10);
    setSelectedCompany(companyId || null);
    setSelectedGrower(null);
    setSelectedFarm(null);
    setGrowers([]);
    setFarms([]);
    setFields([]);
    if (companyId) {
      await fetchGrowers(companyId);
    }
  };

  const handleGrowerChange = async (growerId) => {
    setSelectedGrower(growerId || null);
    setSelectedFarm(null);
    setFarms([]);
    setFields([]);
    if (growerId) {
      await fetchFarms(growerId);
    }
  };

  const handleFarmChange = async (farmId) => {
    setSelectedFarm(farmId || null);
    setFields([]);
    if (farmId) {
      await fetchFields(farmId);
    }
  };

  const addSample = () => {
    setSamples((prevSamples) => {
      const previousSample = prevSamples.length > 0 ? prevSamples[prevSamples.length - 1] : null;
      const newSample = {
        field_id: previousSample?.field_id || null,
        sample_name: '',
        zone: '',
        plot_id: generatePlotID(),
        crop: previousSample?.crop || '',
        yield_goal: previousSample?.yield_goal || '',
        previous_crop: previousSample?.previous_crop || '',
        previous_crop_yield: previousSample?.previous_crop_yield || '',
        lime_history: previousSample?.lime_history ? [...previousSample.lime_history] : [],
        program_level: previousSample?.program_level || 'Excellent',
        organic: previousSample?.organic || false,
        tests: {
          test_b: true,
          test_ca: true,
          test_cu: true,
          test_fe: true,
          test_k: true,
          test_mg: true,
          test_mn: true,
          test_na: true,
          test_om: true,
          test_p2: true,
          test_ph1: true,
          test_s: true,
          test_zn: true,
          test_cl: previousSample?.tests?.test_cl || false,
          test_co: previousSample?.tests?.test_co || false,
          test_mo: previousSample?.tests?.test_mo || false,
          test_salts: previousSample?.tests?.test_salts || false,
          test_bulk_den: previousSample?.tests?.test_bulk_den || false,
          test_olsen: previousSample?.tests?.test_olsen || false,
          test_nh3: previousSample?.tests?.test_nh3 || false,
          test_no3: previousSample?.tests?.test_no3 || false,
          test_ssc: previousSample?.tests?.test_ssc || false
        }
      };
      return [...prevSamples, newSample];
    });
  };

  const updateSample = (index, updatedSample) => {
    setSamples((prevSamples) => {
      const updated = [...prevSamples];
      updated[index] = updatedSample;
      return updated;
    });
  };

  const removeSample = (index) => {
    setSamples((prevSamples) => prevSamples.filter((_, i) => i !== index));
  };

  const getSampleSummary = (sample, idx) => {
    const company = companies.find((c) => c.id === selectedCompany);
    const farm = farms.find((f) => f.id === selectedFarm);
    const field = fields.find((f) => f.id === sample.field_id);

    const firstLine = [];
    if (company) firstLine.push(company.company_name.toUpperCase());
    if (farm) firstLine.push(farm.farm_name.toUpperCase());
    if (field) firstLine.push(field.field_name.toUpperCase());
    if (sample.sample_name) firstLine.push(sample.sample_name.toUpperCase());
    if (sample.zone) firstLine.push(`Zone: ${sample.zone}`);

    const bagId = `${displayBatchNumber}-${idx + 1}`;
    firstLine.push(`Bag: ${bagId}`);

    const optionalTests = [];
    if (sample.tests.test_cl) optionalTests.push('Cl');
    if (sample.tests.test_co) optionalTests.push('Co');
    if (sample.tests.test_mo) optionalTests.push('Mo');
    if (sample.tests.test_salts) optionalTests.push('Salts');
    if (sample.tests.test_bulk_den) optionalTests.push('Bulk Density');
    if (sample.tests.test_olsen) optionalTests.push('Olsen');
    if (sample.tests.test_nh3) optionalTests.push('NH3');
    if (sample.tests.test_no3) optionalTests.push('NO3');
    if (sample.tests.test_ssc) optionalTests.push('SSC');

    const testLine = optionalTests.length > 0
      ? `Standard Analysis, ${optionalTests.join(', ')}`
      : 'Standard Analysis';

    const isQuarantine = company && company.is_outside_us;

    return { firstLine: firstLine.join(' • '), testLine, isQuarantine };
  };

  const submitBatch = async () => {
    if (!selectedCompany || !selectedGrower || samples.length === 0) {
      alert('Please complete all required fields');
      return;
    }

    const payload = {
      company_id: selectedCompany,
      samples: samples.map((s) => ({
        grower_id: selectedGrower,
        farm_id: selectedFarm || null,
        field_id: s.field_id || null,
        sample_name: s.sample_name,
        zone: s.zone,
        plot_id: s.plot_id,
        crop: s.crop,
        yield_goal: s.yield_goal ? parseFloat(s.yield_goal) : null,
        previous_crop: s.previous_crop,
        previous_crop_yield: s.previous_crop_yield ? parseFloat(s.previous_crop_yield) : null,
        lime_history: s.lime_history,
        program_level: s.program_level,
        organic: s.organic,
        tests: s.tests
      }))
    };

    try {
      const res = await authFetch(`${API_BASE}/batches/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Batch ${data.batch_id} created with ${data.sample_count} samples!`);
        onSuccess();
      } else {
        alert('Failed to create batch');
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Submit Soil Samples</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              X
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                <select
                  value={selectedCompany || ''}
                  onChange={handleCompanyChange}
                  disabled={user?.role !== 'admin'}
                  className={`w-full border rounded-lg px-3 py-2 ${user?.role !== 'admin' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                >
                  <option value="">Select Company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grower *</label>
                <SearchableGrowerSelect
                  growers={growers}
                  value={selectedGrower}
                  onChange={handleGrowerChange}
                  disabled={!selectedCompany}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Farm</label>
                <SearchableFarmSelect
                  farms={farms}
                  value={selectedFarm}
                  onChange={handleFarmChange}
                  disabled={!selectedGrower}
                  growerId={selectedGrower}
                  onFarmCreated={fetchFarms}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Samples ({samples.length})</h3>
                <button
                  onClick={addSample}
                  disabled={!selectedGrower}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={16} />
                  Add Sample
                </button>
              </div>

              {samples.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                  <p className="text-gray-500">No samples yet. Click &quot;Add Sample&quot; to get started.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {samples.map((sample, idx) => {
                    const summary = getSampleSummary(sample, idx);
                    return (
                      <div
                        key={idx}
                        className="border rounded-lg p-4 bg-white hover:bg-gray-50 cursor-pointer transition"
                        onClick={() => setEditingSampleIndex(idx)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 mb-1">Sample {idx + 1}</div>
                            <div className="text-sm text-gray-700 mb-1">
                              {summary.firstLine || 'Click to edit'}
                            </div>
                            <div className="text-xs text-gray-600">
                              {summary.testLine}
                              {summary.isQuarantine && (
                                <span className="ml-2 text-red-600 font-semibold">*QUARANTINE</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSample(idx);
                              }}
                              className="text-red-600 hover:text-red-700 text-sm"
                            >
                              Remove
                            </button>
                            <span className="text-gray-400">x</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={submitBatch}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Submit Batch
              </button>
            </div>
          </div>
        </div>
      </div>

      {editingSampleIndex !== null && (
        <SampleDetailModal
          sample={samples[editingSampleIndex]}
          sampleIndex={editingSampleIndex}
          fields={fields}
          selectedFarm={selectedFarm}
          selectedGrower={selectedGrower}
          growers={growers}
          farms={farms}
          onSave={(updatedSample) => {
            updateSample(editingSampleIndex, updatedSample);
            setEditingSampleIndex(null);
          }}
          onCancel={() => setEditingSampleIndex(null)}
          onFieldCreated={fetchFields}
        />
      )}
    </>
  );
}

function SearchableGrowerSelect({ growers, value, onChange, disabled }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Ensure growers is always an array
  const growersList = Array.isArray(growers) ? growers : [];

  const selectedGrower = growersList.find((g) => g.id === value);
  const displayValue = selectedGrower ? selectedGrower.grower_name : '';

  const filteredGrowers = growersList.filter((g) =>
    g.grower_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (grower) => {
    onChange(grower.id);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleClear = () => {
    setSearchTerm('');
    onChange(null);
    setShowDropdown(false);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={showDropdown ? searchTerm : displayValue}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        disabled={disabled}
        className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
        placeholder={disabled ? "Select a company first" : "Search or select grower..."}
      />

      {showDropdown && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredGrowers.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">
              {searchTerm ? `No growers found matching "${searchTerm}"` : 'No growers found'}
            </div>
          ) : (
            filteredGrowers.map((grower) => (
              <div
                key={grower.id}
                onClick={() => handleSelect(grower)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
              >
                {grower.grower_name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SearchableFarmSelect({ farms, value, onChange, disabled, growerId, onFarmCreated }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Ensure farms is always an array
  const farmsList = Array.isArray(farms) ? farms : [];

  const selectedFarm = farmsList.find((f) => f.id === value);
  const displayValue = selectedFarm ? selectedFarm.farm_name : '';

  const filteredFarms = farmsList.filter((f) =>
    f.farm_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exactMatch = filteredFarms.find(
    (f) => f.farm_name.toLowerCase() === searchTerm.toLowerCase()
  );

  const handleSelect = (farm) => {
    onChange(farm.id);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleCreateFarm = async () => {
    const farmName = searchTerm.trim();
    if (!farmName) return;

    if (!growerId) {
      alert('Please select a grower first');
      return;
    }

    setIsCreating(true);

    try {
      const response = await authFetch(`${API_BASE}/farms/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grower_id: growerId,
          farm_name: farmName,
          notes: 'Auto-created during sample submission'
        })
      });

      if (response.ok) {
        const newFarm = await response.json();
        setSearchTerm('');
        setShowDropdown(false);
        setIsCreating(false);
        
        // Call the callback to refresh farms list
        if (onFarmCreated) {
          await onFarmCreated(growerId);
        }
        
        // Select the newly created farm
        onChange(newFarm.id);
      } else {
        const error = await response.json();
        alert(`Failed to create farm: ${error.detail || 'Unknown error'}`);
        setIsCreating(false);
      }
    } catch (err) {
      alert(`Error creating farm: ${err.message}`);
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim() && !exactMatch) {
      e.preventDefault();
      handleCreateFarm();
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={showDropdown ? searchTerm : displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        disabled={disabled || isCreating}
        className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
        placeholder={disabled ? "Select a grower first" : "Type to search or create new farm..."}
      />

      {showDropdown && !disabled && !isCreating && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredFarms.length === 0 ? (
            <div>
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? `No farms found matching "${searchTerm}"` : 'No farms found'}
              </div>
              {searchTerm && (
                <div
                  onClick={handleCreateFarm}
                  className="px-3 py-2 bg-blue-50 hover:bg-blue-100 cursor-pointer text-sm text-blue-700 font-medium border-t"
                >
                  + Create farm "{searchTerm}"
                </div>
              )}
            </div>
          ) : (
            <>
              {filteredFarms.map((farm) => (
                <div
                  key={farm.id}
                  onClick={() => handleSelect(farm)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {farm.farm_name}
                </div>
              ))}
              {searchTerm && !exactMatch && (
                <div
                  onClick={handleCreateFarm}
                  className="px-3 py-2 bg-blue-50 hover:bg-blue-100 cursor-pointer text-sm text-blue-700 font-medium border-t"
                >
                  + Create farm "{searchTerm}"
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isCreating && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg px-3 py-2 text-sm text-gray-600">
          Creating farm...
        </div>
      )}
    </div>
  );
}

function SearchableFieldSelect({ fields, value, onChange, disabled, farmId, growerId, onFieldCreated }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const selectedField = fields.find((f) => f.id === value);
  const displayValue = selectedField ? selectedField.field_name : '';

  const filteredFields = fields.filter((f) =>
    f.field_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exactMatch = filteredFields.find(
    (f) => f.field_name.toLowerCase() === searchTerm.toLowerCase()
  );

  const handleSelect = (field) => {
    onChange(field.id);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleCreateField = async () => {
    const fieldName = searchTerm.trim();
    if (!fieldName) return;

    // If no farm is selected, we need to create a default farm for this grower first
    let targetFarmId = farmId;

    if (!targetFarmId && growerId) {
      try {
        setIsCreating(true);
        // Create a default farm for this grower
        const farmResponse = await authFetch(`${API_BASE}/farms/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            grower_id: growerId,
            farm_name: 'Default Farm',
            notes: 'Auto-created for field submission'
          })
        });

        if (farmResponse.ok) {
          const farmData = await farmResponse.json();
          targetFarmId = farmData.id;
        } else {
          alert('Failed to create default farm');
          setIsCreating(false);
          return;
        }
      } catch (err) {
        alert(`Error creating farm: ${err.message}`);
        setIsCreating(false);
        return;
      }
    }

    if (!targetFarmId) {
      alert('Please select a farm first');
      setIsCreating(false);
      return;
    }

    // Now create the field
    try {
      const response = await authFetch(`${API_BASE}/fields/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farm_id: targetFarmId,
          field_name: fieldName,
          notes: 'Auto-created during sample submission'
        })
      });

      if (response.ok) {
        const newField = await response.json();
        setSearchTerm('');
        setShowDropdown(false);
        setIsCreating(false);
        
        // Call the callback to refresh fields list
        if (onFieldCreated) {
          await onFieldCreated(targetFarmId);
        }
        
        // Select the newly created field
        onChange(newField.id);
      } else {
        const error = await response.json();
        alert(`Failed to create field: ${error.detail || 'Unknown error'}`);
        setIsCreating(false);
      }
    } catch (err) {
      alert(`Error creating field: ${err.message}`);
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchTerm.trim() && !exactMatch) {
      e.preventDefault();
      handleCreateField();
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={showDropdown ? searchTerm : displayValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
        disabled={disabled || isCreating}
        className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
        placeholder={disabled ? "Select a grower first" : "Type to search or create new field..."}
      />

      {showDropdown && !disabled && !isCreating && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredFields.length === 0 ? (
            <div>
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? `No fields found matching "${searchTerm}"` : 'No fields found'}
              </div>
              {searchTerm && (
                <div
                  onClick={handleCreateField}
                  className="px-3 py-2 bg-blue-50 hover:bg-blue-100 cursor-pointer text-sm text-blue-700 font-medium border-t"
                >
                  + Create field "{searchTerm}"
                </div>
              )}
            </div>
          ) : (
            <>
              {filteredFields.map((field) => (
                <div
                  key={field.id}
                  onClick={() => handleSelect(field)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                >
                  {field.field_name}
                  {field.acres && <span className="text-gray-500 ml-2">({field.acres} ac)</span>}
                </div>
              ))}
              {searchTerm && !exactMatch && (
                <div
                  onClick={handleCreateField}
                  className="px-3 py-2 bg-blue-50 hover:bg-blue-100 cursor-pointer text-sm text-blue-700 font-medium border-t"
                >
                  + Create field "{searchTerm}"
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isCreating && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg px-3 py-2 text-sm text-gray-600">
          Creating field...
        </div>
      )}
    </div>
  );
}

function SampleDetailModal({ sample, sampleIndex, fields, selectedFarm, selectedGrower, growers, farms, onSave, onCancel, onFieldCreated }) {
  const [editedSample, setEditedSample] = useState({ ...sample });
  const [showLimeModal, setShowLimeModal] = useState(false);
  const [duplicatePlotId, setDuplicatePlotId] = useState(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [showPlotHistoryModal, setShowPlotHistoryModal] = useState(false);

  const cropList = [
    'ALFALFA',
    'BARLEY',
    'CANOLA',
    'CORN',
    'COTTON',
    'GARDEN',
    'GRAPES',
    'OATS',
    'PEANUTS',
    'RICE',
    'RYE',
    'SORGHUM',
    'SOYBEANS',
    'SUNFLOWERS',
    'WHEAT',
    'NO CROP'
  ];

  // Fetch active custom crop profiles and merge with static list
  const [mergedCropList, setMergedCropList] = useState(cropList);
  useEffect(() => {
    authFetch(`${API_BASE}/crop-profiles`)
      .then((r) => r.json())
      .then((profiles) => {
        const activeNames = profiles
          .filter((p) => p.active)
          .map((p) => p.name.toUpperCase());
        // Merge: static defaults first, then custom profiles (deduplicated)
        const combined = [
          ...cropList,
          ...activeNames.filter((n) => !cropList.includes(n)),
        ];
        setMergedCropList(combined);
      })
      .catch(() => { /* silently fall back to static list */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const checkPlotHistory = async () => {
      const plotId = editedSample.plot_id?.trim();
      if (!plotId || plotId.length < 3) return;

      try {
        const response = await authFetch(`${API_BASE}/plot-history/${plotId}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            const recent = data[0];

            if (!editedSample.crop && recent.crop) {
              updateField('crop', recent.crop);
            }
            if (!editedSample.previous_crop && recent.previous_crop) {
              updateField('previous_crop', recent.previous_crop);
            }
            if (!editedSample.yield_goal && recent.yield_goal) {
              updateField('yield_goal', recent.yield_goal);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch plot history:', err);
      } finally {
        // no-op
      }
    };

    const timer = setTimeout(checkPlotHistory, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSample.plot_id]);

  // Check for duplicate PlotIDs
  useEffect(() => {
    const checkDuplicatePlotId = async () => {
      const plotId = editedSample.plot_id?.trim();
      if (!plotId || plotId.length < 3) {
        setDuplicatePlotId(null);
        setShowDuplicateWarning(false);
        return;
      }

      try {
        const response = await authFetch(`${API_BASE}/check-plotid/${plotId}`);
        if (response.ok) {
          const data = await response.json();
          if (data.exists && data.samples && data.samples.length > 0) {
            setDuplicatePlotId(data);
            setShowDuplicateWarning(true);
          } else {
            setDuplicatePlotId(null);
            setShowDuplicateWarning(false);
          }
        }
      } catch (err) {
        console.error('Failed to check duplicate PlotID:', err);
      }
    };

    const timer = setTimeout(checkDuplicatePlotId, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedSample.plot_id]);

  const updateField = (field, value) => {
    setEditedSample((prev) => ({ ...prev, [field]: value }));
  };

  const updateTest = (testName, value) => {
    setEditedSample((prev) => ({
      ...prev,
      tests: { ...prev.tests, [testName]: value }
    }));
  };

  const addLimeEntry = (entry) => {
    setEditedSample((prev) => ({
      ...prev,
      lime_history: [...(prev.lime_history || []), entry]
    }));
  };

  const removeLimeEntry = (index) => {
    setEditedSample((prev) => ({
      ...prev,
      lime_history: prev.lime_history.filter((_, i) => i !== index)
    }));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-bold">Sample {sampleIndex + 1} Details</h2>
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
              <X size={16} />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Recommendation Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Program Level</label>
                  <select
                    value={editedSample.program_level}
                    onChange={(e) => updateField('program_level', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="Excellent">Excellent</option>
                    <option value="Building">Building</option>
                    <option value="Minimal">Minimal</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Determines fertilizer recommendation intensity</p>
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <input
                      type="checkbox"
                      checked={editedSample.organic}
                      onChange={(e) => updateField('organic', e.target.checked)}
                      className="rounded"
                    />
                    Organic Program
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Use organic-approved fertilizers only</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Plot ID *</label>
                  <input
                    value={editedSample.plot_id}
                    onChange={(e) => updateField('plot_id', e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 ${showDuplicateWarning ? 'border-yellow-500' : ''}`}
                    placeholder="e.g., A4729"
                  />
                  {showDuplicateWarning && duplicatePlotId && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-yellow-600 font-bold text-lg">⚠️</span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-yellow-800">
                            Warning: PlotID "{editedSample.plot_id}" already exists!
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            This PlotID has been used in {duplicatePlotId.count} previous sample(s). 
                            PlotIDs should be unique field location identifiers.
                          </p>
                          <div className="mt-2 text-xs text-gray-700 bg-white p-2 rounded border border-yellow-200">
                            <p className="font-semibold mb-1">Most recent usage:</p>
                            <p><strong>Batch:</strong> {duplicatePlotId.samples[0].batch_id}</p>
                            {duplicatePlotId.samples[0].company_name && (
                              <p><strong>Company:</strong> {duplicatePlotId.samples[0].company_name}</p>
                            )}
                            {duplicatePlotId.samples[0].grower_name && (
                              <p><strong>Grower:</strong> {duplicatePlotId.samples[0].grower_name}</p>
                            )}
                            {duplicatePlotId.samples[0].farm_name && (
                              <p><strong>Farm:</strong> {duplicatePlotId.samples[0].farm_name}</p>
                            )}
                            {duplicatePlotId.samples[0].field_name && (
                              <p><strong>Field:</strong> {duplicatePlotId.samples[0].field_name}</p>
                            )}
                            {duplicatePlotId.samples[0].crop && (
                              <p><strong>Crop:</strong> {duplicatePlotId.samples[0].crop}</p>
                            )}
                            {duplicatePlotId.samples[0].created_at && (
                              <p><strong>Date:</strong> {new Date(duplicatePlotId.samples[0].created_at).toLocaleDateString()}</p>
                            )}
                          </div>
                          <p className="text-xs text-yellow-700 mt-2 font-medium">
                            Please verify this is correct or use a different PlotID.
                          </p>
                          {duplicatePlotId.count > 1 && (
                            <button
                              type="button"
                              onClick={() => setShowPlotHistoryModal(true)}
                              className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline font-medium"
                            >
                              View all {duplicatePlotId.count} previous uses →
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Field</label>
                  <SearchableFieldSelect
                    fields={fields}
                    value={editedSample.field_id}
                    onChange={(fieldId) => updateField('field_id', fieldId)}
                    disabled={!selectedGrower}
                    farmId={selectedFarm}
                    growerId={selectedGrower}
                    onFieldCreated={onFieldCreated}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sample Name</label>
                  <input
                    value={editedSample.sample_name}
                    onChange={(e) => updateField('sample_name', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Optional sample identifier"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Zone</label>
                  <input
                    value={editedSample.zone}
                    onChange={(e) => updateField('zone', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Optional zone identifier"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Crop</label>
                  <input
                    list="crop-list"
                    value={editedSample.crop}
                    onChange={(e) => updateField('crop', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Type or select: Corn, Soybeans, etc."
                  />
                  <datalist id="crop-list">
                    {mergedCropList.map((crop) => (
                      <option key={crop} value={crop} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Yield Goal (unit/ac)</label>
                  <input
                    type="number"
                    value={editedSample.yield_goal}
                    onChange={(e) => updateField('yield_goal', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., 200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Previous Crop</label>
                  <input
                    list="prev-crop-list"
                    value={editedSample.previous_crop}
                    onChange={(e) => updateField('previous_crop', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Type or select previous crop"
                  />
                  <datalist id="prev-crop-list">
                    {mergedCropList.map((crop) => (
                      <option key={crop} value={crop} />
                    ))}
                  </datalist>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Previous Crop Yield (unit/ac)
                  </label>
                  <input
                    type="number"
                    value={editedSample.previous_crop_yield}
                    onChange={(e) => updateField('previous_crop_yield', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="e.g., 180"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Lime History</h3>
                <button
                  onClick={() => setShowLimeModal(true)}
                  className="flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  <Plus size={16} />
                  Add Lime
                </button>
              </div>

              {editedSample.lime_history && editedSample.lime_history.length > 0 ? (
                <div className="space-y-2">
                  {editedSample.lime_history.map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                      <div className="text-sm">
                        <span className="font-medium">{entry.type}</span>
                        {' • '}
                        <span>
                          {new Date(entry.year, entry.month - 1).toLocaleDateString('en-US', {
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                        {' • '}
                        <span>{entry.amount_lbs_ac} lbs/ac</span>
                      </div>
                      <button onClick={() => removeLimeEntry(idx)} className="text-red-600 hover:text-red-700">
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">No lime history recorded</div>
              )}
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Soil Tests</h3>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm text-gray-600">
                  ✓ Standard tests included: B, Ca, Cu, Fe, K, Mg, Mn, Na, OM, P2, pH, S, Zn
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Optional Tests</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'test_cl', label: 'Chloride (Cl)' },
                    { key: 'test_co', label: 'Cobalt (Co)' },
                    { key: 'test_mo', label: 'Molybdenum (Mo)' },
                    { key: 'test_salts', label: 'Salts' },
                    { key: 'test_bulk_den', label: 'Bulk Density' },
                    { key: 'test_olsen', label: 'Olsen P' },
                    { key: 'test_nh3', label: 'NH3' },
                    { key: 'test_no3', label: 'NO3' },
                    { key: 'test_ssc', label: 'Sand/Silt/Clay' }
                  ].map((test) => (
                    <label key={test.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={editedSample.tests[test.key]}
                        onChange={(e) => updateTest(test.key, e.target.checked)}
                        className="rounded"
                      />
                      {test.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
                Cancel
              </button>
              <button
                onClick={() => onSave(editedSample)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Sample
              </button>
            </div>
          </div>
        </div>
      </div>

      {showLimeModal && (
        <LimeHistoryModal
          onSave={addLimeEntry}
          onCancel={() => setShowLimeModal(false)}
        />
      )}

      {/* Plot History Modal */}
      {showPlotHistoryModal && duplicatePlotId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">
                Plot ID History: "{editedSample.plot_id}"
              </h3>
              <button
                onClick={() => setShowPlotHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                This Plot ID has been used <strong>{duplicatePlotId.count}</strong> time(s) previously. 
                PlotIDs should be unique to a specific geographic location.
              </p>

              <div className="space-y-4">
                {duplicatePlotId.samples.map((sample, index) => (
                  <div
                    key={sample.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-gray-900">
                        Use #{index + 1} {index === 0 && <span className="text-xs text-blue-600 ml-2">(Most Recent)</span>}
                      </h4>
                      <span className="text-xs text-gray-500">
                        {sample.created_at ? new Date(sample.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        }) : 'Unknown date'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">Batch ID:</span>
                        <span className="ml-2 font-medium text-gray-900">{sample.batch_id}</span>
                      </div>

                      {sample.company_name && (
                        <div>
                          <span className="text-gray-500">Company:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.company_name}</span>
                        </div>
                      )}

                      {sample.grower_name && (
                        <div>
                          <span className="text-gray-500">Grower:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.grower_name}</span>
                        </div>
                      )}

                      {sample.farm_name && (
                        <div>
                          <span className="text-gray-500">Farm:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.farm_name}</span>
                        </div>
                      )}

                      {sample.field_name && (
                        <div>
                          <span className="text-gray-500">Field:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.field_name}</span>
                        </div>
                      )}

                      {sample.crop && (
                        <div>
                          <span className="text-gray-500">Crop:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.crop}</span>
                        </div>
                      )}

                      {sample.previous_crop && (
                        <div>
                          <span className="text-gray-500">Previous Crop:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.previous_crop}</span>
                        </div>
                      )}

                      {sample.zone && (
                        <div>
                          <span className="text-gray-500">Zone/Depth:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.zone}</span>
                        </div>
                      )}

                      {sample.batch_status && (
                        <div>
                          <span className="text-gray-500">Status:</span>
                          <span className="ml-2 font-medium text-gray-900">{sample.batch_status}</span>
                        </div>
                      )}
                    </div>

                    {/* Highlight if different grower/farm than current */}
                    {(() => {
                      const currentGrowerName = growers.find(g => g.id === selectedGrower)?.grower_name;
                      const currentFarmName = farms.find(f => f.id === selectedFarm)?.farm_name;
                      const isDifferent = sample.grower_name !== currentGrowerName || sample.farm_name !== currentFarmName;
                      
                      return isDifferent && (
                        <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded p-2">
                          <p className="text-xs text-yellow-800 font-medium">
                            ⚠️ Warning: This usage was for a different grower/farm than your current selection!
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 text-sm mb-2">
                  ℹ️ About Plot IDs
                </h4>
                <p className="text-xs text-blue-800">
                  Plot IDs should represent a specific geographic location. If you're sampling the same 
                  physical location as before, reusing the Plot ID is correct and helps track changes over time. 
                  If this is a different location, please use a unique Plot ID.
                </p>
              </div>
            </div>

            <div className="border-t px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowPlotHistoryModal(false)}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LimeHistoryModal({ onSave, onCancel }) {
  const [limeType, setLimeType] = useState('Calcium Carbonate');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [amount, setAmount] = useState('');

  const limeTypes = ['Calcium Carbonate', 'Dolomite', 'Gypsum'];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

  const handleSave = () => {
    if (!amount) {
      alert('Please enter an amount');
      return;
    }

    onSave({
      type: limeType,
      month,
      year,
      amount_lbs_ac: parseFloat(amount)
    });
    onCancel();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">Add Lime Application</h3>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lime Type *</label>
            <select
              value={limeType}
              onChange={(e) => setLimeType(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
            >
              {limeTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Month *</label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="w-full border rounded-lg px-3 py-2"
              >
                {months.map((m, idx) => (
                  <option key={m} value={idx + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                className="w-full border rounded-lg px-3 py-2"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Amount (lbs/ac) *</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full border rounded-lg px-3 py-2"
              placeholder="e.g., 2000"
              min="0"
              step="100"
            />
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button onClick={handleSave} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Add Lime
          </button>
        </div>
      </div>
    </div>
  );
}

export default SubmitModal;
